import { ReactNode, SubmitEvent, SubmitEventHandler, useState } from 'react';

import { cn } from '@/lib/utils';

import { Button, ButtonProps } from './ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog/Dialog';

export interface ModalProps {
  show: boolean;
  onClose: CallableFunction;
  title?: string;
  description?: ReactNode;
  submitLabel?: string;
  submitButtonVariant?: ButtonProps['variant'];
  submitDisabled?: boolean;
  cancelLabel?: string;
  cancelDisabled?: boolean;
  processing?: boolean;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | '8xl';
  /**
   * Cap the modal at the viewport height and let the body fill the remaining
   * space instead of the whole dialog scrolling. The body must own its own
   * scroll regions (flex-1 + overflow) for this to be useful.
   */
  fitHeight?: boolean;
  /**
   * Take over the whole viewport below the `sm` breakpoint. Use for large,
   * multi-step modals that feel cramped as a floating phone dialog.
   */
  fullScreenOnMobile?: boolean;
  closeable?: boolean;
  /**
   * When false, clicking the backdrop or pressing Escape no longer closes the modal — only the ×
   * button or an explicit Cancel/submit action does. Use for modals holding work that a stray click
   * shouldn't discard (e.g. a running debug session).
   */
  dismissible?: boolean;
  onSubmit?: SubmitEventHandler<HTMLFormElement>;
  onCancelClick?: CallableFunction;
  children?: ReactNode;
  /**
   * Extra actions rendered in the trailing group, immediately left of the submit button.
   * Cancel stays alone at the leading edge.
   */
  footerActions?: ReactNode;
  portalContainerId?: string;
}

export function Modal({
  children,
  title,
  description,
  submitLabel,
  onSubmit,
  submitButtonVariant = 'default',
  submitDisabled = false,
  cancelLabel = 'Cancel',
  cancelDisabled = false,
  processing = false,
  show,
  maxWidth = '2xl',
  fitHeight = false,
  fullScreenOnMobile = false,
  closeable = true,
  dismissible = true,
  onClose,
  onCancelClick,
  footerActions,
  portalContainerId
}: ModalProps) {
  function close() {
    if (closeable) {
      onClose();
    }
  }

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(() =>
    portalContainerId ? document.getElementById(portalContainerId) : null
  );

  // Re-resolve the portal container when the target id changes. Adjusting during render keeps the
  // container in sync with the prop without the extra paint an effect would introduce.
  const [prevPortalContainerId, setPrevPortalContainerId] = useState(portalContainerId);
  if (portalContainerId !== prevPortalContainerId) {
    setPrevPortalContainerId(portalContainerId);
    setPortalContainer(portalContainerId ? document.getElementById(portalContainerId) : null);
  }

  const maxWidthClass = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
    '4xl': 'sm:max-w-4xl',
    '5xl': 'sm:max-w-5xl',
    '6xl': 'sm:max-w-6xl',
    '7xl': 'sm:max-w-7xl',
    '8xl': 'sm:max-w-[90rem]'
  }[maxWidth];

  function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    e.stopPropagation();
    // The submit button disables while processing, but Enter still fires the
    // form's submit event; re-submitting mid-flight cancels the in-flight
    // Inertia visit and re-posts, so its success/error callbacks never run.
    if (processing) {
      return;
    }
    onSubmit?.(e);
  }

  function handleCancelClick() {
    if (onCancelClick) {
      onCancelClick();
      return;
    }

    onClose();
  }

  // A footer with other actions puts Cancel alone at the leading edge as the quiet way
  // out; a footer whose only button dismisses keeps it at the trailing edge.
  const hasActions = Boolean(submitLabel || footerActions);
  const cancelButton = cancelLabel ? (
    <Button
      type="button"
      variant={hasActions ? 'ghost' : 'outline'}
      onClick={handleCancelClick}
      disabled={cancelDisabled}
    >
      {cancelLabel}
    </Button>
  ) : null;

  return (
    <Dialog open={show} onOpenChange={close}>
      <DialogContent
        className={cn(
          maxWidthClass,
          fitHeight && 'flex h-[90vh] flex-col overflow-y-auto sm:overflow-hidden',
          fullScreenOnMobile &&
            'max-sm:top-0 max-sm:left-0 max-sm:h-dvh max-sm:max-h-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:grid-rows-[minmax(0,1fr)] max-sm:overflow-hidden max-sm:border-none'
        )}
        closeable={closeable}
        container={portalContainer}
        {...(!description ? { 'aria-describedby': undefined } : {})}
        {...(dismissible
          ? {}
          : {
              // Backdrop click and Escape no longer dismiss — only the × or Cancel closes.
              onInteractOutside: (event: Event) => event.preventDefault(),
              onEscapeKeyDown: (event: KeyboardEvent) => event.preventDefault()
            })}
      >
        <form
          onSubmit={handleSubmit}
          className={cn(
            fitHeight ? 'flex min-h-0 flex-1 flex-col gap-4' : 'grid grid-cols-1 gap-8',
            // Pin the header and footer while the body scrolls, instead of
            // letting the grid stretch rows apart across the full height.
            fullScreenOnMobile &&
              !fitHeight &&
              'max-sm:min-h-0 max-sm:grid-rows-[auto_minmax(0,1fr)_auto] max-sm:gap-5'
          )}
        >
          {title || description ? (
            <DialogHeader>
              {title ? <DialogTitle>{title}</DialogTitle> : null}
              {description ? <DialogDescription>{description}</DialogDescription> : null}
            </DialogHeader>
          ) : null}

          {children ? (
            <div
              className={cn(
                'text-foreground',
                // grid-cols-1 sizes the column with minmax(0, 1fr) so wide
                // children shrink instead of stretching the dialog.
                fitHeight ? 'flex min-h-0 flex-1 flex-col gap-4' : 'grid grid-cols-1 gap-4',
                // The scroll region clips horizontal overflow, so pad it out
                // by the same amount it is pulled in to keep focus rings and
                // card hover shadows from being cut off at its edges.
                fullScreenOnMobile &&
                  !fitHeight &&
                  'max-sm:-mx-3 max-sm:-my-1 max-sm:min-h-0 max-sm:content-start max-sm:overflow-y-auto max-sm:px-3 max-sm:py-1'
              )}
            >
              {children}
            </div>
          ) : null}

          <DialogFooter start={hasActions ? cancelButton : undefined}>
            {hasActions ? (
              <>
                {footerActions}
                {submitLabel ? (
                  <Button
                    type="submit"
                    loading={processing}
                    disabled={submitDisabled}
                    variant={submitButtonVariant}
                  >
                    {submitLabel}
                  </Button>
                ) : null}
              </>
            ) : (
              cancelButton
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
